// Per-entity query-key factories. Each entity root is shaped
// `all → lists → list → details → detail` so a single prefix reconciles every
// view of that entity (TkDodo "Effective React Query Keys"). Detail nests under
// the entity root, so invalidating `projectKeys.lists()` never touches a detail
// — the property the write policy in `reconcile.ts` relies on.
//
// Convention: `.claude/rules/frontend/tanstack-query.md`.

import type { AgentKind, ProjectSortKey } from '@core/api-clients/types.js';

export type ProjectListFilters = {
  sort: ProjectSortKey;
  q: string;
};

export const projectKeys = {
  all: ['projects'] as const,
  lists: () => [...projectKeys.all, 'list'] as const,
  list: (teamId: string, filters: ProjectListFilters) =>
    [...projectKeys.lists(), teamId, filters] as const,
  // First page only, for the switcher's compact views — distinct tail so the
  // shape never collides with the infinite list under the same filters.
  firstPage: (teamId: string, filters: ProjectListFilters & { limit: number }) =>
    [...projectKeys.lists(), teamId, 'first-page', filters] as const,
  recents: (teamId: string) => [...projectKeys.lists(), teamId, 'recents'] as const,
  details: () => [...projectKeys.all, 'detail'] as const,
  detail: (projectId: string) => [...projectKeys.details(), projectId] as const,
};

// A project owns its report(s). The REST surface is project-scoped today
// (`/projects/:projectId/report`, one report per project), so the report cache
// is keyed by `projectId` — the only identifier available at read time. When
// the multi-report list endpoint lands, `detail` re-keys to `reportId` and a
// `list(projectId)` query of the project's reports appears alongside it.
export const reportKeys = {
  all: ['reports'] as const,
  details: () => [...reportKeys.all, 'detail'] as const,
  detail: (projectId: string) => [...reportKeys.details(), projectId] as const,
};

// Project-scoped batch of signed attachment download URLs (warm cache).
export const attachmentKeys = {
  all: ['attachments'] as const,
  forProject: (projectId: string) => [...attachmentKeys.all, projectId] as const,
};

// Session singletons — no list/detail split.
export const meKeys = { all: ['me'] as const };
export const teamKeys = { all: ['teams'] as const };
export const organisationKeys = { current: ['organisations', 'current'] as const };
export const reportSpecKeys = { all: ['report-specs'] as const };

// Org-scoped entitlement snapshot (plan + per-feature usage/limit).
export const entitlementKeys = { all: ['entitlements'] as const };

// Agent sessions: per-(project, kind) session state + the me-scoped vault
// status. The live SSE stream is deliberately NOT query state (design § SPA).
export const agentSessionKeys = {
  all: ['agent-sessions'] as const,
  states: () => [...agentSessionKeys.all, 'state'] as const,
  projectStates: (projectId: string) =>
    [...agentSessionKeys.states(), projectId] as const,
  state: (projectId: string, kind: AgentKind) =>
    [...agentSessionKeys.projectStates(projectId), kind] as const,
  vault: () => [...agentSessionKeys.all, 'vault'] as const,
};

// Quickscan v2 memory viewer — the per-project derived dataset, read-only
// (no reconcile: nothing in the SPA writes the memory).
export const memoryViewerKeys = {
  all: ['memory-viewer'] as const,
  detail: (projectId: string) => [...memoryViewerKeys.all, projectId] as const,
};

// Release toggles — only the self-service drawer's list today (the enabled-keys
// snapshot rides on `me`).
export const releaseToggleKeys = {
  all: ['release-toggles'] as const,
  selfService: () => [...releaseToggleKeys.all, 'self-service'] as const,
};
